import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  TextField,
  Button,
  Grid,
  Typography,
  Card,
  CardContent,
  IconButton,
  Alert,
  LinearProgress,
  MenuItem,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import { PhotoCamera, Delete as DeleteIcon, CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import { KYCLevel1, KYCStatus } from '../types/kyc';
import { ipfsUriToUrl } from '../utils/ipfs';
import ImagePreview from './ImagePreview';

interface KYCLevel1FormProps {
   KYCStatus?:KYCStatus;
  initialData?: KYCLevel1;
  onSubmit: (data: KYCLevel1, files: { idFrontImage?: File; idBackImage?: File }) => Promise<void>;
  submitting: boolean;
}

// Yup validation rules
const validationSchema = Yup.object({
  realName: Yup.string().required('Please enter your real name'),
  gender: Yup.string().required('Please select your gender'),
  age: Yup.number()
    .required('Please enter your age')
    .min(1, 'Age must be greater than 0')
    .max(150, 'Age cannot exceed 150'),
  occupation: Yup.string().required('Please enter your occupation'),
  address: Yup.string().required('Please enter your address'),
  idType: Yup.string().required('Please select your ID type'),
  idNumber: Yup.string().required('Please enter your ID number'),
  idFrontImage: Yup.mixed().required('Please upload the front photo of your ID'),
  idBackImage: Yup.mixed().required('Please upload the back photo of your ID')
});

const KYCLevel1Form: React.FC<KYCLevel1FormProps> = ({
   KYCStatus,
  initialData,
  onSubmit,
  submitting
}) => {
  const [idFrontPreview, setIdFrontPreview] = useState<string | null>(null);
  const [idBackPreview, setIdBackPreview] = useState<string | null>(null);
  
  // Formik配置
  const formik = useFormik<KYCLevel1>({
    initialValues: {
      realName: '',
      gender: '',
      age: undefined,
      occupation: '',
      address: '',
      idType: '',
      idNumber: '',
      idFrontImage: '',
      idBackImage: ''
    },
    validationSchema,
    onSubmit: async (values) => {
      const files: { idFrontImage?: File; idBackImage?: File } = {};
      
      // 检查是否为File对象（新上传的文件）
      if (values.idFrontImage && typeof values.idFrontImage === 'object' && (values.idFrontImage as any) instanceof File) {
        files.idFrontImage = values.idFrontImage as File;
      }
      if (values.idBackImage && typeof values.idBackImage === 'object' && (values.idBackImage as any) instanceof File) {
        files.idBackImage = values.idBackImage as File;
      }
      
      await onSubmit(values, files);
    }
  });

  const genderOptions = [
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
    { value: 'other', label: 'Other' }
  ];

  const idTypeOptions = [
    { value: 'id_card', label: 'ID Card' },
    { value: 'passport', label: 'Passport' },
    { value: 'driver_license', label: 'Driver\'s License' },
    { value: 'other', label: 'Other' }
  ];

  // 处理初始数据
  useEffect(() => {
    if (initialData) {
      formik.setValues(initialData);
      // 如果有现有图片URL，设置预览
      if (initialData.idFrontImage && typeof initialData.idFrontImage === 'string') {
        setIdFrontPreview(ipfsUriToUrl(initialData.idFrontImage));
      }
      if (initialData.idBackImage && typeof initialData.idBackImage === 'string') {
        setIdBackPreview(ipfsUriToUrl(initialData.idBackImage));
      }
    }
  }, [initialData]);

  // 创建文件预览URL
  const createPreviewUrl = (file: File): string => {
    return URL.createObjectURL(file);
  };

  // 清理预览URL
  const cleanupPreviewUrl = (url: string | null) => {
    if (url && url.startsWith('blob:')) {
      URL.revokeObjectURL(url);
    }
  };

  // 处理文件选择
  const handleFileSelect = (field: 'idFrontImage' | 'idBackImage') => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      // 设置文件到formik
      formik.setFieldValue(field, file);
      
      // 创建预览
      const previewUrl = createPreviewUrl(file);
      if (field === 'idFrontImage') {
        cleanupPreviewUrl(idFrontPreview);
        setIdFrontPreview(previewUrl);
      } else {
        cleanupPreviewUrl(idBackPreview);
        setIdBackPreview(previewUrl);
      }
    };

    input.click();
  };

  // 移除文件
  const handleRemoveFile = (field: 'idFrontImage' | 'idBackImage') => {
    formik.setFieldValue(field, '');
    
    if (field === 'idFrontImage') {
      cleanupPreviewUrl(idFrontPreview);
      setIdFrontPreview(null);
    } else {
      cleanupPreviewUrl(idBackPreview);
      setIdBackPreview(null);
    }
  };

  // 清理预览URL
  useEffect(() => {
    return () => {
      cleanupPreviewUrl(idFrontPreview);
      cleanupPreviewUrl(idBackPreview);
    };
  }, []);



  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Identity Verification Information
        </Typography>
        
        <Alert severity="warning" sx={{ mb: 2 }}>
          Please ensure all information is true and valid. False information will lead to verification failure.
        </Alert>
        

        
        {submitting && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress />
            <Typography variant="body2" sx={{ mt: 1 }}>
              Submitting...
            </Typography>
          </Box>
        )}
        
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={3}>
            {/* Personal Basic Information */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Real Name *"
                name="realName"
                value={formik.values.realName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                fullWidth
                error={formik.touched.realName && Boolean(formik.errors.realName)}
                helperText={formik.touched.realName && formik.errors.realName}
                disabled={submitting}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={formik.touched.gender && Boolean(formik.errors.gender)} disabled={submitting}>
                <InputLabel>Gender *</InputLabel>
                <Select
                  name="gender"
                  value={formik.values.gender}
                  label="Gender *"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  {genderOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.gender && formik.errors.gender && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                    {formik.errors.gender}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="Age *"
                name="age"
                type="number"
                value={formik.values.age || ''}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                fullWidth
                error={formik.touched.age && Boolean(formik.errors.age)}
                helperText={formik.touched.age && formik.errors.age}
                disabled={submitting}
                inputProps={{ min: 1, max: 150 }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="Occupation *"
                name="occupation"
                value={formik.values.occupation}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                fullWidth
                error={formik.touched.occupation && Boolean(formik.errors.occupation)}
                helperText={formik.touched.occupation && formik.errors.occupation}
                disabled={submitting}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Address *"
                name="address"
                value={formik.values.address}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                fullWidth
                multiline
                rows={2}
                error={formik.touched.address && Boolean(formik.errors.address)}
                helperText={formik.touched.address && formik.errors.address}
                disabled={submitting}
              />
            </Grid>
          
          {/* ID Information */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              ID Information
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={formik.touched.idType && Boolean(formik.errors.idType)} disabled={submitting}>
              <InputLabel>ID Type *</InputLabel>
              <Select
                name="idType"
                value={formik.values.idType}
                label="ID Type *"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              >
                {idTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.idType && formik.errors.idType && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                  {formik.errors.idType}
                </Typography>
              )}
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              label="ID Number *"
              name="idNumber"
              value={formik.values.idNumber}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              fullWidth
              error={formik.touched.idNumber && Boolean(formik.errors.idNumber)}
              helperText={formik.touched.idNumber && formik.errors.idNumber}
              disabled={submitting}
            />
          </Grid>
          
          {/* ID Photos Upload */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              ID Photos
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Front Photo of ID *
              </Typography>
              
              {idFrontPreview ? (
                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                  <ImagePreview
                    src={idFrontPreview}
                    alt="Front of ID"
                    style={{ width: 200, height: 120, objectFit: 'cover', borderRadius: 8 }}
                  />
                  <IconButton
                    size="small"
                    sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'background.paper' }}
                    onClick={() => handleRemoveFile('idFrontImage')}
                    disabled={submitting}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <Button
                  variant="outlined"
                  startIcon={<PhotoCamera />}
                  onClick={() => handleFileSelect('idFrontImage')}
                  disabled={submitting}
                  color={formik.touched.idFrontImage && formik.errors.idFrontImage ? 'error' : 'primary'}
                >
                  Upload Front Photo
                </Button>
              )}
              
              {formik.touched.idFrontImage && formik.errors.idFrontImage && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  {formik.errors.idFrontImage}
                </Typography>
              )}
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Back Photo of ID *
              </Typography>
              
              {idBackPreview ? (
                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                  <ImagePreview
                    src={idBackPreview}
                    alt="Back of ID"
                    style={{ width: 200, height: 120, objectFit: 'cover', borderRadius: 8 }}
                  />
                  <IconButton
                    size="small"
                    sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'background.paper' }}
                    onClick={() => handleRemoveFile('idBackImage')}
                    disabled={submitting}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <Button
                  variant="outlined"
                  startIcon={<PhotoCamera />}
                  onClick={() => handleFileSelect('idBackImage')}
                  disabled={submitting}
                  color={formik.touched.idBackImage && formik.errors.idBackImage ? 'error' : 'primary'}
                >
                  Upload Back Photo
                </Button>
              )}
              
              {formik.touched.idBackImage && formik.errors.idBackImage && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  {formik.errors.idBackImage}
                </Typography>
              )}
            </Box>
          </Grid>
          
          {/* 提交按钮 */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={submitting||KYCStatus==2||KYCStatus==3}
              >
                {submitting ? 'Submitting...' : 'Submit for Verification'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
      </CardContent>
    </Card>
  );
};

export default KYCLevel1Form;